import dynamic from 'next/dynamic';
import { memo } from 'react';
import { withErrorBoundary } from '@/ErrorBoundary';
import { useScreenSize } from '~/contexts/DeviceContext';
import { useUser } from '~/contexts/UserContext';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import useClientSide from '~/hooks/useClientSide';

const ActivationModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ActivationModal" */ '@/Modals/ActivationModal'
    ),
  { ssr: false }
);

const CreateSavedSearchModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "CreateSavedSearchModal" */ '@/Modals/SearchAlert/CreateSavedSearchModal'
    ),
  { ssr: false }
);

const FilterModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "FilterModal" */ '@/MainLayout/MobileFilters/FilterModal'
    ),
  { ssr: false }
);

const GeolocationModal = dynamic(
  () => import(/* webpackChunkName: "GeolocationModal" */ './GeolocationModal'),
  { ssr: false }
);

const ManageSavedSearchesModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ManageSavedSearchesModal" */ '@/Modals/SearchAlert/ManageSavedSearchesModal'
    ),
  { ssr: false }
);

const MoreDetailsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoreDetailsModal" */ '@/Modals/MoreDetailsModal'
    ),
  { ssr: false }
);

const SoftPullMfeModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SoftPullMfeModal" */ '@/Modals/SoftPullMfeModal/SoftPullMfeModal'
    ),
  { ssr: false }
);

const WIPNotificationsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "WIPNotificationsModal" */ '@/Modals/WIPNotifications/WIPNotificationsModal'
    )
);

type OwnProps = {
  loadActivationModal: boolean;
  loadAuthedModals: boolean;
  loadFilterModal: boolean;
  loadGeolocationModal: boolean;
  loadMoreDetailsModal: boolean;
  loadSoftPullModal: boolean;
};

const ModalsContent = memo(
  ({
    loadActivationModal,
    loadAuthedModals,
    loadFilterModal,
    loadGeolocationModal,
    loadMoreDetailsModal,
    loadSoftPullModal
  }: OwnProps) => (
    <>
      {loadActivationModal && <ActivationModal />}
      {loadAuthedModals && (
        <>
          <CreateSavedSearchModal />
          <ManageSavedSearchesModal />
          <WIPNotificationsModal />
        </>
      )}
      {loadFilterModal && <FilterModal />}
      {loadGeolocationModal && <GeolocationModal />}
      {loadMoreDetailsModal && <MoreDetailsModal />}
      {loadSoftPullModal && <SoftPullMfeModal />}
    </>
  )
);

function ModalsState() {
  const user = useUser();
  const isClientSide = useClientSide();
  const { isDesktop } = useScreenSize();

  const isLoggedAndLoaded = user.isLoaded && user.data.isLoggedIn;
  const isInTileRedesignExperiment = useCheckExperimentBucket(
    'srch-tile-redesign-2023',
    'experiment'
  );

  return (
    <ModalsContent
      loadActivationModal
      loadAuthedModals={isLoggedAndLoaded}
      loadFilterModal={!isDesktop}
      loadGeolocationModal={isClientSide}
      loadMoreDetailsModal={isInTileRedesignExperiment}
      loadSoftPullModal
    />
  );
}

export default withErrorBoundary(ModalsState);
